import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PackagingItemCreateRequest,
  PackagingItemListQuery,
  PackagingItemListResponse,
  PackagingItemResponse,
  PackagingItemUpdateRequest,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: (q: Partial<PackagingItemListQuery>) => ['packaging-items', 'list', q] as const,
  one: (id: string) => ['packaging-items', 'one', id] as const,
};

function toQueryString(q: Partial<PackagingItemListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.category) params.set('category', q.category);
  if (q.search) params.set('search', q.search);
  if (q.active !== undefined) params.set('active', String(q.active));
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function usePackagingItems(query: Partial<PackagingItemListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () =>
      api<PackagingItemListResponse>(`/api/packaging-items${toQueryString(query)}`),
    staleTime: 30_000,
  });
}

export function usePackagingItem(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['packaging-items', 'one', 'NIL'],
    queryFn: () => api<PackagingItemResponse>(`/api/packaging-items/${id}`),
    enabled: !!id,
  });
}

export function useCreatePackagingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PackagingItemCreateRequest) =>
      api<PackagingItemResponse>('/api/packaging-items', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['packaging-items', 'list'] });
    },
  });
}

export function useUpdatePackagingItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PackagingItemUpdateRequest) =>
      api<PackagingItemResponse>(`/api/packaging-items/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['packaging-items', 'list'] });
    },
  });
}

export function useDeletePackagingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/packaging-items/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['packaging-items', 'list'] });
    },
  });
}
